import { useMemo, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';

/**
 * 3D 图形拼装谜题(空间图形维度的"测"关)
 *
 * 交互简化说明: 规格里设想的是"旋转+拖拽",这里简化为"旋转+点选放置"
 * (先点一个积木选中,再点目标凹槽放进去)—— 对 6-8 岁孩子来说,点选比
 * 精细拖拽容错率更高,命中区域也更大,是刻意的低龄可用性取舍。
 */

function geometryFor(shape) {
  switch (shape) {
    case 'pyramid':
      return <coneGeometry args={[0.6, 1.1, 4]} />;
    case 'sphere':
      return <sphereGeometry args={[0.55, 20, 20]} />;
    case 'cube':
    default:
      return <boxGeometry args={[0.9, 0.9, 0.9]} />;
  }
}

function SlotGhost({ slot, position, filled, onClick }) {
  return (
    <mesh position={position} onClick={onClick} name={`slot-${slot.id}`}>
      {geometryFor(slot.shape)}
      <meshStandardMaterial
        color={slot.color}
        transparent
        opacity={filled ? 0.95 : 0.25}
        wireframe={!filled}
      />
    </mesh>
  );
}

function PieceMesh({ piece, position, selected, onClick }) {
  return (
    <mesh
      position={[position[0], position[1] + (selected ? 0.25 : 0), position[2]]}
      onClick={onClick}
      name={`piece-${piece.id}`}
    >
      {geometryFor(piece.shape)}
      <meshStandardMaterial
        color={piece.color}
        emissive={selected ? piece.color : '#000000'}
        emissiveIntensity={selected ? 0.6 : 0}
      />
    </mesh>
  );
}

function SpatialPuzzle3D({ puzzle, onComplete }) {
  const { slots, pieces } = puzzle.params;
  const [placements, setPlacements] = useState({});
  const [selectedPieceId, setSelectedPieceId] = useState(null);

  // 凹槽悬在底座后半边,积木摆在底座前沿的台面上 —— 积木必须在底座(y=-0.35)
  // 之上:以前放在 y=-1.2,手机默认视角下被底座整个挡住,只露出画面底边一条色块
  const slotPositions = useMemo(
    () => slots.map((_, i) => [(i - (slots.length - 1) / 2) * 1.6, 1.0, -0.9]),
    [slots]
  );
  const piecePositions = useMemo(
    () => pieces.map((_, i) => [(i - (pieces.length - 1) / 2) * 1.25, 0.15, 1.7]),
    [pieces]
  );

  const placedPieceIds = new Set(Object.values(placements));
  const availablePieces = pieces.filter((p) => !placedPieceIds.has(p.id));

  const handlePieceClick = (pieceId) => {
    setSelectedPieceId((prev) => (prev === pieceId ? null : pieceId));
  };

  const handleSlotClick = (slotId) => {
    if (placements[slotId]) {
      // 已放置的凹槽再次点击 = 取消放置,把积木放回去(允许反悔,零挫败)
      setPlacements((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
      return;
    }
    if (selectedPieceId) {
      setPlacements((prev) => ({ ...prev, [slotId]: selectedPieceId }));
      setSelectedPieceId(null);
    }
  };

  const allFilled = Object.keys(placements).length === slots.length;

  useEffect(() => {
    if (allFilled) {
      const timer = setTimeout(() => onComplete(placements), 500);
      return () => clearTimeout(timer);
    }
  }, [allFilled, placements, onComplete]);

  return (
    <div className="w-full">
      <div
        className="h-80 w-full rounded-2xl border-2 border-black bg-amber-50 shadow-brutal overflow-hidden"
        data-testid="spatial-puzzle-canvas"
      >
        <Canvas camera={{ position: [0, 3.6, 7], fov: 50 }}>
          <ambientLight intensity={0.8} />
          <directionalLight position={[3, 5, 2]} intensity={0.7} />
          <OrbitControls enablePan={false} minDistance={4} maxDistance={9} />

          <mesh position={[0, -0.35, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[3.4, 32]} />
            <meshStandardMaterial color="#F5E6C8" />
          </mesh>

          {slots.map((slot, i) => (
            <SlotGhost
              key={slot.id}
              slot={slot}
              position={slotPositions[i]}
              filled={!!placements[slot.id]}
              onClick={() => handleSlotClick(slot.id)}
            />
          ))}

          {availablePieces.map((piece) => {
            const originalIndex = pieces.findIndex((p) => p.id === piece.id);
            return (
              <PieceMesh
                key={piece.id}
                piece={piece}
                position={piecePositions[originalIndex]}
                selected={selectedPieceId === piece.id}
                onClick={() => handlePieceClick(piece.id)}
              />
            );
          })}
        </Canvas>
      </div>
      <p className="mt-3 text-center text-sm text-gray-500">
        拖动画面转一转底座,点一个积木选中,再点凹槽把它放进去吧!
      </p>
    </div>
  );
}

export default SpatialPuzzle3D;
